/**
 * BlockCard — redigerbart block-kort i IntroEditor.
 */
import React from 'react';
import {
  Box, Typography, TextField, IconButton, Chip, Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';

const accent = '#E05500';

export default function BlockCard({ block, index, onChange, onDelete }) {
  const updateField = (field, val) => onChange({ ...block, [field]: val });

  const removeKeyword = (i) => {
    updateField('keywords', (block.keywords || []).filter((_, idx) => idx !== i));
  };

  const addKeyword = () => {
    // eslint-disable-next-line no-alert
    const kw = window.prompt('Nytt stödord:');
    if (kw?.trim()) updateField('keywords', [...(block.keywords || []), kw.trim()]);
  };

  return (
    <Box sx={{
      border: '1px solid', borderColor: 'divider', borderRadius: 2,
      p: 2, mb: 2, bgcolor: 'background.paper',
    }}>
      {/* Title row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <DragIndicatorIcon sx={{ color: 'text.disabled', cursor: 'grab', flexShrink: 0, fontSize: 20 }} />
        <Box sx={{
          width: 26, height: 26, borderRadius: '50%',
          bgcolor: accent + '20', border: `1px solid ${accent}44`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Typography fontWeight={800} fontSize="0.7rem" color={accent}>{index + 1}</Typography>
        </Box>
        <TextField
          size="small"
          placeholder="Avsnittsnamn"
          value={block.label || ''}
          onChange={e => updateField('label', e.target.value)}
          sx={{ flex: 1, '& .MuiInputBase-root': { fontWeight: 700 } }}
        />
        <IconButton size="small" onClick={onDelete}
          sx={{ color: 'text.disabled', '&:hover': { color: '#ef4444' } }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Keywords */}
      <Box sx={{ mb: 2 }}>
        <Typography fontSize="0.65rem" fontWeight={700} color="text.secondary" mb={0.75} letterSpacing={0.5}>
          STÖDORD
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
          {(block.keywords || []).map((kw, i) => (
            <Chip key={i} label={kw} size="small" onDelete={() => removeKeyword(i)}
              sx={{
                bgcolor: accent + '14', color: 'text.primary',
                border: `1px solid ${accent}44`, fontWeight: 600, fontSize: '0.72rem',
              }} />
          ))}
          <IconButton size="small" onClick={addKeyword}
            sx={{ width: 24, height: 24, bgcolor: 'action.hover', '&:hover': { bgcolor: 'action.selected' } }}>
            <AddIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Speaker text + public fields */}
      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>

        {/* Left: speaker text */}
        <Box sx={{ flex: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography fontSize="0.65rem" fontWeight={700} color="text.secondary" letterSpacing={0.5} sx={{ flex: 1 }}>
              LÖPTEXT
            </Typography>
            <Tooltip title={block.speakerTextVisible ? 'Synlig för deltagare på publik skärm — klicka för att göra privat' : 'Privat — bara facilitatorn ser. Klicka för att visa på publik skärm'}>
              <Box
                onClick={() => updateField('speakerTextVisible', !block.speakerTextVisible)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.4,
                  px: 0.75, py: 0.2, borderRadius: 1, cursor: 'pointer',
                  bgcolor: block.speakerTextVisible ? accent + '18' : 'action.hover',
                  border: '1px solid', borderColor: block.speakerTextVisible ? accent + '55' : 'divider',
                  color: block.speakerTextVisible ? accent : 'text.disabled',
                  '&:hover': { opacity: 0.8 },
                  transition: 'all 0.15s',
                }}
              >
                {block.speakerTextVisible
                  ? <LockOpenIcon sx={{ fontSize: 11 }} />
                  : <LockIcon sx={{ fontSize: 11 }} />}
                <Typography fontSize="0.6rem" fontWeight={700} letterSpacing={0.3}>
                  {block.speakerTextVisible ? 'Synlig' : 'Privat'}
                </Typography>
              </Box>
            </Tooltip>
          </Box>
          <TextField
            fullWidth multiline minRows={3}
            placeholder={block.speakerTextVisible ? 'Löptext — visas på publik skärm' : 'Löptext — bara i talarstödet'}
            value={block.speakerText || ''}
            onChange={e => updateField('speakerText', e.target.value)}
            size="small"
          />
        </Box>

        {/* Right: public screen */}
        <Box sx={{ flex: 1 }}>
          <Typography fontSize="0.65rem" fontWeight={700} color="text.secondary" mb={0.5} letterSpacing={0.5}>
            PUBLIK SKÄRM
          </Typography>
          <TextField
            fullWidth size="small"
            placeholder="Rubrik (max 6 ord)"
            value={block.publicTitle || ''}
            onChange={e => updateField('publicTitle', e.target.value)}
            sx={{ mb: 1, '& .MuiInputBase-root': { fontWeight: 600 } }}
          />
          <TextField
            fullWidth size="small"
            placeholder="Citat / nyckelrad (valfritt)"
            value={block.publicQuote || ''}
            onChange={e => updateField('publicQuote', e.target.value)}
          />
        </Box>
      </Box>
    </Box>
  );
}
